import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Truck, ShieldCheck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import apiFetch, { baseURL } from '../../utils/api';
import './Checkout.css';

const PAK_CITIES = [
    "Karachi", "Lahore", "Faisalabad", "Rawalpindi", "Gujranwala", "Peshawar", "Multan", 
    "Hyderabad", "Islamabad", "Quetta", "Bahawalpur", "Sargodha", "Sialkot", "Sukkur", 
    "Larkana", "Shekhupura", "Rahim Yar Khan", "Jhang", "Dera Ghazi Khan", "Gujrat", 
    "Sahiwal", "Wah Cantonment", "Mardan", "Kasur", "Okara", "Mingora", "Nawabshah", 
    "Chiniot", "Kotri", "Kāmoke", "Hafizabad", "Sadiqabad", "Mirpur Khas", "Burewala", 
    "Kohat", "Khanewal", "Dera Ismail Khan", "Turbat", "Muzaffargarh", "Abbottabad", 
    "Jhelum", "Shikarpur", "Mandi Bahauddin", "Tando Allahyar", "Hub", "Sambrial", 
    "Vihari", "Jacobabad", "Ahmadpur East", "Sialkot Cantonment", "Kotli", "Tando Adam",
    "Khairpur", "Dera Murad Jamali", "Pishin", "Chaman", "Zhob", "Khuzdar", "Barkhan"
].sort();

const Checkout = () => {
    const { cartItems, getCartTotal, clearCart } = useCart();
    const [submitting, setSubmitting] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [lastOrderId, setLastOrderId] = useState(null);
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        city: 'Lahore',
        state: '',
        postalCode: '',
        country: 'Pakistan',
        paymentMethod: 'Cash on Delivery'
    });

    const subtotal = getCartTotal();
    const shipping = 250;
    const total = subtotal + shipping;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const orderData = {
            ...formData,
            items: cartItems.map(item => ({
                productId: item.id,
                quantity: item.quantity
            }))
        };

        try {
            const response = await apiFetch('/api/orders/create', {
                method: 'POST',
                body: JSON.stringify(orderData)
            });
            const result = await response.json();

            if (result.success) {
                setLastOrderId(result.orderId);
                setOrderSuccess(true);
                clearCart();
            } else {
                alert(result.message || 'Failed to place order.');
            }
        } catch (error) {
            alert('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (orderSuccess) {
        return (
            <div className="ec-checkout-success">
                <div className="ec-success-card">
                    <CheckCircle size={80} color="#6366f1" />
                    <h2>Order Placed Successfully!</h2>
                    <p>Thank you for shopping with us. Your order ID is <b>#{lastOrderId}</b>.</p>
                    <p>Our team will contact you at <b>{formData.phone}</b> for verification.</p>
                    <Link to="/home" className="ec-btn ec-btn--primary">Back to Home</Link>
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="ec-checkout-empty">
                <h2>No items to checkout</h2>
                <Link to="/shop" className="ec-btn ec-btn--primary">Go to Shop</Link>
            </div>
        );
    }

    return (
        <div className="ec-chk-root">
            <div className="ec-chk-container">
                <div className="ec-chk-grid">
                    {/* Left: Shipping Form */}
                    <div className="ec-chk-form-wrap">
                        <Link to="/cart" className="ec-back-link">
                            <ArrowLeft size={16} /> Back to Cart
                        </Link>
                        <h1>Shipping Information</h1>
                        <p className="ec-chk-desc">Provide your details below to secure your order.</p>

                        <form onSubmit={handleSubmit} className="ec-chk-form">
                            <div className="ec-form-section">
                                <h3>Contact Information</h3>
                                <div className="ec-form-row">
                                    <div className="ec-form-group">
                                        <label>Email Address</label>
                                        <input 
                                            type="email" name="email" required 
                                            placeholder="john@example.com"
                                            value={formData.email} onChange={handleChange}
                                        />
                                    </div>
                                    <div className="ec-form-group">
                                        <label>Phone Number</label>
                                        <input 
                                            type="tel" name="phone" required 
                                            placeholder="03xx xxxxxxx"
                                            value={formData.phone} onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="ec-form-section">
                                <h3>Shipping Address</h3>
                                <div className="ec-form-row">
                                    <div className="ec-form-group">
                                        <label>First Name</label>
                                        <input 
                                            type="text" name="firstName" required 
                                            placeholder="e.g. John"
                                            value={formData.firstName} onChange={handleChange}
                                        />
                                    </div>
                                    <div className="ec-form-group">
                                        <label>Last Name</label>
                                        <input 
                                            type="text" name="lastName" required 
                                            placeholder="e.g. Doe"
                                            value={formData.lastName} onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="ec-form-group">
                                    <label>Street Address</label>
                                    <input 
                                        type="text" name="addressLine1" required 
                                        placeholder="House #, Street name, Area"
                                        value={formData.addressLine1} onChange={handleChange}
                                    />
                                </div>

                                <div className="ec-form-group">
                                    <label>Apartment, suite, etc. (optional)</label>
                                    <input 
                                        type="text" name="addressLine2" 
                                        placeholder="Apartment, suite, unit, etc."
                                        value={formData.addressLine2} onChange={handleChange}
                                    />
                                </div>

                                <div className="ec-form-row three">
                                    <div className="ec-form-group">
                                        <label>City</label>
                                        <select name="city" value={formData.city} onChange={handleChange} required>
                                            {PAK_CITIES.map(city => (
                                                <option key={city} value={city}>{city}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="ec-form-group">
                                        <label>State / Province</label>
                                        <input 
                                            type="text" name="state" required 
                                            placeholder="e.g. Punjab"
                                            value={formData.state} onChange={handleChange}
                                        />
                                    </div>
                                    <div className="ec-form-group">
                                        <label>Postal Code</label>
                                        <input 
                                            type="text" name="postalCode" required 
                                            placeholder="e.g. 54000"
                                            value={formData.postalCode} onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="ec-payment-sec">
                                <h3>Payment Method</h3>
                                <div className="ec-payment-option active">
                                    <div className="ec-opt-check"><div className="inner"></div></div>
                                    <div className="ec-opt-details">
                                        <span>Cash on Delivery</span>
                                        <p>Pay with cash when your order reaches your doorstep.</p>
                                    </div>
                                    <span className="ec-opt-fee">+Rs. 250</span>
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className={`ec-place-btn ${submitting ? 'loading' : ''}`}
                                disabled={submitting}
                            >
                                {submitting ? 'Processing...' : 'Complete Purchase'}
                            </button>
                        </form>
                    </div>

                    {/* Right: Order Summary */}
                    <aside className="ec-chk-summary">
                        <div className="ec-chk-summary-inner">
                            <h3>Order Summary</h3>
                            <div className="ec-summary-items">
                                {cartItems.map(item => (
                                    <div key={item.id} className="ec-summary-item">
                                        <div className="ec-s-item-info">
                                            <div className="ec-s-img">
                                                <img src={item.image ? (item.image.startsWith('http') ? item.image : `${baseURL}${item.image}`) : 'https://via.placeholder.com/100'} alt="" />
                                                <span className="ec-s-badge">{item.quantity}</span>
                                            </div>
                                            <span className="ec-s-name">{item.name}</span>
                                        </div>
                                        <span className="ec-s-price">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="ec-chk-totals">
                                <div className="ec-chk-row">
                                    <span>Subtotal</span>
                                    <span>Rs. {subtotal.toLocaleString()}</span>
                                </div>
                                <div className="ec-chk-row">
                                    <span>Shipping</span>
                                    <span>Rs. {shipping.toLocaleString()}</span>
                                </div>
                                <div className="ec-chk-row ec-total">
                                    <span>Total Amount</span>
                                    <span>Rs. {total.toLocaleString()}</span>
                                </div>
                            </div>

                            <div className="ec-chk-trust">
                                <div className="ec-trust-item">
                                    <Truck size={16} /> <span>Insured Express Shipping</span>
                                </div>
                                <div className="ec-trust-item">
                                    <ShieldCheck size={16} /> <span>Secure SSL Encrypted Checkout</span>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
